import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import { isEmptyArr } from 'shared/utils/isEmpty';
import { useAppDispatch } from 'app/store';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useGetCountriesQuery } from 'features/getCountries/api/getCountriesApi';
import { Controller, useForm } from 'react-hook-form';
import { useGetCitiesSetQuery, useDeleteCitiesMutation } from 'features/useCity/api/useCityApi';
import { CityCreateWidget } from 'widgets/settings/CityCreateWidget';

type FormDataType = { name: string; id: number | null; country_id: number | null };

const loader = (
  <Stack alignItems="center">
    <CircularProgress />
  </Stack>
);

export const CitiesWidget = () => {
  const [country, setCountry] = useState<number | null>(null);
  const [city, setCity] = useState<FormDataType>();

  const cities = useGetCitiesSetQuery({ country_id: country });
  const countries = useGetCountriesQuery(null);
  const [deleteCity] = useDeleteCitiesMutation();

  const dispatch = useAppDispatch();

  const { control, setValue } = useForm<FormDataType>({
    defaultValues: { name: '', id: null },
  });

  const handleDeleteCity = async (id: number) => {
    const data = await deleteCity({ id });
    if (data) {
      cities.refetch();
    }
  };

  const handleGetCountryOfCity = () => {
    if (countries.data && city) {
      return countries.data.find((item) => item.id === city.country_id);
    }
    return null;
  };

  if (cities.isFetching) return loader;

  if (cities.isError) {
    return <Stack alignItems="center">ERROR</Stack>;
  }

  return (
    <>
      {cities.isSuccess ? (
        <Box
          component={StyledCard}
          sx={{ marginBottom: '24px' }}
        >
          <TableContainer>
            <Table
              sx={{ maxWidth: 500 }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '10px 0' }}>ID</TableCell>
                  <TableCell sx={{ padding: '10px' }}>Город</TableCell>
                  <TableCell>
                    {!countries.isFetching
                      ? countries.isSuccess &&
                        !isEmptyArr(countries.data) && (
                          <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth>
                                <InputLabel id="country-select-label">Страна</InputLabel>
                                <Select
                                  sx={{ minWidth: '120px' }}
                                  labelId="country-select-label"
                                  id="country-simple-select"
                                  label="Страна"
                                  {...field}
                                >
                                  {countries.data.map((item) => (
                                    <MenuItem
                                      onClick={() => {
                                        setValue('id', item.id);
                                        setCountry(item.id);
                                      }}
                                      key={item.id}
                                      value={item.name}
                                    >
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                        )
                      : loader}
                  </TableCell>
                </TableRow>
              </TableHead>

              {!isEmptyArr(cities.data) ? (
                <TableBody>
                  {cities.data.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell
                        sx={{ padding: '10px 0' }}
                        component="th"
                        scope="row"
                      >
                        {item.id}
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">
                        <EditOutlinedIcon
                          onClick={() => {
                            setCity(item);
                            dispatch(setDrawerOpen(true));
                          }}
                        />
                        <DeleteOutlinedIcon onClick={() => handleDeleteCity(item.id)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <Typography sx={{ marginTop: '24px' }}>Нет городов</Typography>
              )}
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      <CityCreateWidget
        city={city}
        cityCountry={handleGetCountryOfCity}
        setCity={setCity}
        refetch={cities.refetch}
      />
    </>
  );
};
